import React, { useState, useEffect } from 'react';
import './Projects.css';

const useNarrowProjectsLayout = () => {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const onChange = () => setNarrow(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return narrow;
};

/** Tablet + mobile: grouped phone screenshots (e.g. Tripy) need a nested 2-column grid */
const usePhoneGridProjectsLayout = () => {
  const [match, setMatch] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1024px)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1024px)');
    const onChange = () => setMatch(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return match;
};

const isPhoneImageBox = (box) => box.type === 'image' && box.variant === 'phone';

const ProjectBox = ({
  type,
  content,
  size,
  images,
  currentImageIndex,
  onNextImage,
  onPrevImage,
  rowSpan,
  rowStart,
  rowEnd,
  align,
  boxIndex,
  animationOrder,
  variant,
  narrowLayout,
}) => {
  const rowStyle = rowStart && rowEnd ? { gridRow: `${rowStart} / ${rowEnd}` } : {};
  const rowSpanClass = rowSpan ? `project-box-row-span-${rowSpan}` : '';

  if (type === 'image') {
    const phoneClass = variant === 'phone' ? 'project-box-image--phone' : '';
    const hasImages = images && images.length > 0;

    if (narrowLayout && hasImages && images.length > 1) {
      return (
        <div className={`project-box project-box-${size} project-box-image project-box-image--mobile-stack ${rowSpanClass} ${phoneClass}`} style={rowStyle}>
          <div className="project-image-stack">
            {images.map((src, i) => (
              <img
                key={`${i}-${src}`}
                src={src}
                alt={`Project screenshot ${i + 1}`}
                className="project-box-image-content"
              />
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className={`project-box project-box-${size} project-box-image ${rowSpanClass} ${phoneClass}`} style={rowStyle}>
        <div className="project-image-wrapper">
          {hasImages ? (
            <>
              <img
                src={images[currentImageIndex]}
                alt={`Project screenshot ${currentImageIndex + 1}`}
                className="project-box-image-content"
              />
              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    className="carousel-arrow carousel-arrow-left"
                    onClick={onPrevImage}
                    aria-label="Previous image"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    className="carousel-arrow carousel-arrow-right"
                    onClick={onNextImage}
                    aria-label="Next image"
                  >
                    ›
                  </button>
                  <div className="carousel-indicator">
                    {currentImageIndex + 1} / {images.length}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="project-placeholder">Project Image</div>
          )}
        </div>
      </div>
    );
  }

  if (type === 'text') {
    const alignClass = align === 'justify' ? 'project-box-text--justify' : align === 'left' ? 'project-box-text--left' : '';
    const variantClass = variant === 'tripy-intro' ? 'project-box-text--tripy-intro' : '';
    return (
      <div className={`project-box project-box-${size} project-box-text ${rowSpanClass} ${alignClass} ${variantClass}`} style={rowStyle}>
        <div className="project-box-content">
          {content}
        </div>
      </div>
    );
  }

  if (type === 'title') {
    const isStudoConnect = content === 'Studo Connect Website';
    return (
      <div
        className={`project-box project-box-${size} project-box-title ${rowSpanClass} ${isStudoConnect ? 'project-box-title--studo-connect' : ''}`}
        style={rowStyle}
      >
        <div className="project-box-title-inner">
          <h3 className="project-box-title-text">{content}</h3>
          {isStudoConnect && (
            <p className="project-box-title-subtitle">
              I worked on Studo Connect website during my work at Studo GmbH. I gained experience in web development through implementing features and working on both front-end and back-end. However my work was mostly focued on front-end.
            </p>
          )}
        </div>
      </div>
    );
  }

  return null;
};

const Project = ({ project }) => {
  const narrowLayout = useNarrowProjectsLayout();
  const phoneGridLayout = usePhoneGridProjectsLayout();
  const [imageIndices, setImageIndices] = useState({});

  const handleNextImage = (boxIndex) => {
    const box = project.boxes[boxIndex];
    if (box.type === 'image' && box.images) {
      const currentIndex = imageIndices[boxIndex] || 0;
      setImageIndices({
        ...imageIndices,
        [boxIndex]: (currentIndex + 1) % box.images.length
      });
    }
  };

  const handlePrevImage = (boxIndex) => {
    const box = project.boxes[boxIndex];
    if (box.type === 'image' && box.images) {
      const currentIndex = imageIndices[boxIndex] || 0;
      setImageIndices({
        ...imageIndices,
        [boxIndex]: (currentIndex - 1 + box.images.length) % box.images.length
      });
    }
  };

  const projectBoxAt = (index) => {
    const box = project.boxes[index];
    return (
      <ProjectBox
        key={index}
        boxIndex={index}
        animationOrder={box.animationOrder}
        type={box.type}
        content={box.content}
        size={box.size}
        images={box.images}
        currentImageIndex={imageIndices[index] || 0}
        onNextImage={() => handleNextImage(index)}
        onPrevImage={() => handlePrevImage(index)}
        rowSpan={box.rowSpan}
        rowStart={box.rowStart}
        rowEnd={box.rowEnd}
        align={box.align}
        variant={box.variant}
        narrowLayout={narrowLayout}
      />
    );
  };

  let boxNodes;
  if (phoneGridLayout && project.phoneGridOnMobile) {
    boxNodes = [];
    let i = 0;
    while (i < project.boxes.length) {
      const box = project.boxes[i];
      if (isPhoneImageBox(box)) {
        const start = i;
        const indices = [];
        while (i < project.boxes.length && isPhoneImageBox(project.boxes[i])) {
          indices.push(i);
          i += 1;
        }
        boxNodes.push(
          <div key={`phone-grid-${start}`} className="project-phone-grid">
            {indices.map((idx) => projectBoxAt(idx))}
          </div>
        );
      } else {
        boxNodes.push(projectBoxAt(i));
        i += 1;
      }
    }
  } else {
    boxNodes = project.boxes.map((_, index) => projectBoxAt(index));
  }

  return (
    <div className="project-container">
      <div className={`project-boxes-grid ${project.compactFirstRow ? 'project-boxes-grid--compact-first' : ''}`}>
        {boxNodes}
      </div>
      {project.description && (
        <div className="project-description-container">
          <p className="project-description">{project.description}</p>
          {project.links && (
            <div className="project-links">
              {project.links.demo && (
                <a
                  href={project.links.demo}
                  className="project-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Live Demo"
                >
                  Live Demo
                </a>
              )}
              {project.links.github && (
                <a
                  href={project.links.github}
                  className="project-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="GitHub"
                >
                  GitHub
                </a>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Projects = () => {
  // Example projects - update with your actual project data
  // Each project has multiple boxes with different sizes
  // Box sizes should add up to 12 columns per row (e.g., 4+8, 5+7, 6+6, 3+9)
  const projects = [
    {
      boxes: [
        {
          type: 'title',
          size: 'equal', // 6 columns
          rowStart: 1,
          rowEnd: 2, // Row 1 only (shorter)
          content: 'Studo Connect Website'
        },
        {
          type: 'image',
          size: 'equal', // 6 columns (6+6=12)
          rowStart: 1,
          rowEnd: 2, // Row 1 only (shorter for wide image)
          images: [
            '/images/connect_project/pic1.png',
            // Add more images if needed
          ]
        },
        {
          type: 'image',
          size: 'equal', // 6 columns
          rowStart: 2,
          rowEnd: 4, // Rows 2-3 (taller)
          images: [
            '/images/connect_project/pic2.png',
            // Add more images if needed
          ]
        },
        {
          type: 'image',
          size: 'equal', // 6 columns
          rowStart: 2,
          rowEnd: 3, // Row 2 only (fills empty space)
          images: [
            '/images/connect_project/pic3.png',
            // Add more images if needed
          ]
        },
        {
          type: 'text',
          size: 'equal', // 6 columns (6+6=12)
          rowStart: 3,
          rowEnd: 4, // Row 3 only (shorter)
          align: 'justify',
          animationOrder: 2,
          content: (
            <>
              <strong>What is Studo Connect?</strong>
              <br />
              A website that works like a bridge between the university partners in Studo and the app for students. Universitites or Organizations can post on news feed, manage their feeds, check analytics and further more.
              <br />
              <br />
              <strong>What did I do there?</strong>
              <br />
              I implemented features such as mobile preview for news feed, in-app menu item editor and creator and many more features. (You can view on the screenshots)
            </>
          )
        }
      ],
    },
    {
      boxes: [
        {
          type: 'text',
          size: 'medium', // 5 columns
          rowStart: 1,
          rowEnd: 2,
          variant: 'tripy-intro',
          content: (
            <>
              <span className="tripy-title">Tripy</span>
              <span className="tripy-tagline">Travel Planning mobile app</span>
            </>
          )
        },
        {
          type: 'text',
          size: 'large', // 7 columns
          rowStart: 1,
          rowEnd: 2,
          align: 'left',
          content: (
            <>
              I worked on this app alongside with{' '}
              <a href="https://abushaqra.net/" target="_blank" rel="noopener noreferrer">Mohamed Abushaqra</a> as a side project for both of us.
              We created a mobile app for easier travel planning using AI. I worked mainly on the front-end and all the design and the illustrations you see in the app inside the screenshots. Therefore, this was a start for me into mobile app interface design. 
            </>
          )
        },
        {
          type: 'image',
          size: 'third',
          rowStart: 2,
          rowEnd: 3,
          variant: 'phone',
          images: ['/images/tripy_1.png']
        },
        {
          type: 'image',
          size: 'third',
          rowStart: 2,
          rowEnd: 3,
          variant: 'phone',
          images: ['/images/tripy_2.png']
        },
        {
          type: 'image',
          size: 'third',
          rowStart: 2,
          rowEnd: 3,
          variant: 'phone',
          images: ['/images/tripy_3.png']
        },
        {
          type: 'image',
          size: 'third',
          rowStart: 2,
          rowEnd: 3,
          variant: 'phone',
          images: ['/images/tripy_4.png']
        }
      ],
      compactFirstRow: true,
      phoneGridOnMobile: true
    }
  ];

  return (
    <section
      id="projects"
      className="projects"
      style={{
        position: 'relative',
        background: 'linear-gradient(135deg, #19338a 0%, #1e40af 50%, #3b82f6 100%)'
      }}
    >
      <div className="section-container">
        <h2 className="section-title">Projects</h2>
        <div className="projects-list">
          {projects.map((project, index) => (
            <Project key={index} project={project} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
